import { DisplayMode, FetchState, RiskEligibility } from './index';
import { Money, Currency } from '@square/dashboard-protos/models/squareup/connect/v2/common/money';
import { MIN_BITCOIN_DISPLAY_AMOUNT } from '@banking/ui/utils/constants';
import { InvestmentAccountMetadata } from '@square/dashboard-protos/models/squareup/invest/model';
const zeroBTC = Money.create({
  amount: 0,
  currency: Currency.BTC
});
const zeroUSD = Money.create({
  amount: 0,
  currency: Currency.USD
});
export const pendingBalance = state => {
  const pendingBalance = state.btcAccount?.pendingBalance;
  if (pendingBalance) {
    return pendingBalance;
  }
  if (state.displayMode === DisplayMode.FIAT_MODE && state.canSeeBitcoinFiatConversion) {
    return zeroUSD;
  }
  return zeroBTC;
};
export const balance = state => state.btcAccount?.currentBalance || zeroBTC;
export const marketValue = state => state.btcAccount?.marketValue || zeroUSD;
export const cashAppIdentifier = state => {
  if (state.btcAccount?.cashappIdentifier) {
    return `$${state.btcAccount?.cashappIdentifier}`;
  } else if (state.cashtag) {
    // Weirdly enough, the cashtag that comes back from grantly comes back
    // with the $ included so we do not need it here
    return `${state.cashtag}`;
  } else {
    return null;
  }
};
export const primaryBalance = state => {
  if (!state.canSeeBitcoinFiatConversion) {
    return balance(state);
  }
  if (state.displayMode === DisplayMode.FIAT_MODE) {
    return marketValue(state);
  }
  return balance(state);
};
export const secondaryBalance = state => {
  if (!state.canSeeBitcoinFiatConversion || state.displayMode === DisplayMode.FIAT_MODE) {
    return balance(state);
  }
  return marketValue(state);
};
export const showPrimaryAsCents = state => {
  if (!state.canSeeBitcoinFiatConversion) {
    return balance(state).amount < MIN_BITCOIN_DISPLAY_AMOUNT;
  }
  return state.displayMode === DisplayMode.SATOSHI_MODE;
};
export const showSecondaryAsCents = state => {
  if (!state.canSeeBitcoinFiatConversion) {
    return balance(state).amount >= MIN_BITCOIN_DISPLAY_AMOUNT;
  }
  return state.displayMode === DisplayMode.FIAT_MODE && balance(state).amount < MIN_BITCOIN_DISPLAY_AMOUNT;
};
export const isInvestmentAccountPendingWithholding = state => {
  return Boolean(state?.btcAccount?.accountStatus === InvestmentAccountMetadata.AccountStatus.PENDING_WITHHOLDING);
};
export const isInvestmentAccountFullyCreated = state => {
  return Boolean(state?.btcAccount && state?.btcAccount?.accountStatus !== InvestmentAccountMetadata.AccountStatus.PENDING_WITHHOLDING);
};
export const shouldSkipLinking = state => {
  return Boolean(!isInvestmentAccountFullyCreated(state) && state.cashtag && state.grantId);
};
export const isInvestmentAccountDeactivated = state => {
  return Boolean(state.accounts.some(account => account.accountStatus === InvestmentAccountMetadata.AccountStatus.DEACTIVATED));
};
export const displayMode = state => state.displayMode;
export const activities = state => {
  return state.activities.map(activity => {
    let primaryAmount = activity.toAmount;
    let secondaryAmount = activity.fromAmount;
    let showActivityPrimaryAsCents = (primaryAmount?.amount || 0) < MIN_BITCOIN_DISPLAY_AMOUNT;
    let showActivitySecondaryAsCents = false;
    if (state.canSeeBitcoinFiatConversion) {
      if (displayMode(state) === DisplayMode.SATOSHI_MODE) {
        showActivityPrimaryAsCents = true;
      }
      if (displayMode(state) === DisplayMode.FIAT_MODE) {
        primaryAmount = activity.fromAmount;
        secondaryAmount = activity.toAmount;
        showActivityPrimaryAsCents = false;
        showActivitySecondaryAsCents = (secondaryAmount?.amount || 0) < MIN_BITCOIN_DISPLAY_AMOUNT;
      }
    }
    return {
      ...activity,
      primaryAmount,
      secondaryAmount,
      showActivityPrimaryAsCents,
      showActivitySecondaryAsCents
    };
  });
};
export const isWaitlistedForInvestment = state => state.isInBusinessTypeWaitlist || state.isInRiskWaitlist;
export const isInvestmentModalOpen = state => state.isModalOpen;
export const hasInvestmentAccount = state => Boolean(state?.btcAccount);
export const isFetchingInvestmentAccounts = state => state.listInvestmentAccountState === FetchState.LOADING;
export const isFetchingInvestmentAccountsAndEligibility = state => state.listInvestmentAccountState === FetchState.LOADING || state.riskEligibility === RiskEligibility.LOADING;
export const failedToFetchInvestmentDataForBankingHome = state => {
  return state.listInvestmentAccountState === FetchState.ERROR || state.riskEligibility === RiskEligibility.ERROR && !isInvestmentAccountFullyCreated(state);
};
export const isRiskIneligibleForInvestment = state => state.riskEligibility === RiskEligibility.INELIGIBLE;